import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import {
  CheckCircle, XCircle, Edit, Upload, AlertTriangle,
  Users, X, Search, Save, RefreshCw, ClipboardList, Send,  
} from 'lucide-react';
import Layout from '../../components/Layout/Layout';

const PERFILES_LIDER = [
  { value: 'coordinador_general',     label: 'Coordinador General' },
  { value: 'coordinador_territorial', label: 'Coordinador Territorial' },
  { value: 'coordinador_sectorial',   label: 'Coordinador Sectorial' },
  { value: 'seccional',               label: 'Seccional' },
  { value: 'operador',                label: 'Operador' },
  { value: 'promotor',                label: 'Promotor' },
];

const ESTATUS_BADGE = {
  pendiente:  'bg-amber-100 text-amber-700 border-amber-200',
  confirmado: 'bg-green-100 text-green-700 border-green-200',
  descartado: 'bg-red-100 text-red-700 border-red-200',
};

export default function ValidacionMasiva() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [resumen, setResumen]                 = useState({ pendientes: 0, confirmados: 0, descartados: 0, duplicados: 0 });
  const [personas, setPersonas]               = useState([]);
  const [loading, setLoading]                 = useState(false);
  const [subiendo, setSubiendo]               = useState(false);
  const [mensaje, setMensaje]                 = useState(null);
  const [secciones, setSecciones]             = useState([]);
  const [filtro, setFiltro]                   = useState('pendiente');
  const [subTabPendientes, setSubTabPendientes] = useState('sin_contactar');
  const [busqueda, setBusqueda]               = useState('');
  const [estructuras, setEstructuras]         = useState([]);
  const [lideres, setLideres]                 = useState([]);
  const [estructuraExcel, setEstructuraExcel] = useState('');
  const [perfilLiderExcel, setPerfilLiderExcel] = useState('');
  const [liderExcel, setLiderExcel]           = useState('');
  const [archivoExcel, setArchivoExcel]       = useState(null);
  const [mensajeValidacion, setMensajeValidacion] = useState('');
  const [editandoId, setEditandoId]           = useState(null);
  const [formEditar, setFormEditar]           = useState({});
  const [enviandoSMS, setEnviandoSMS]         = useState(false);

  useEffect(() => {
    fetchResumen();
    fetchSecciones();
    fetchPendientes();
    fetchEstructurasYLideres();
    traerMensaje();
  }, []);

  useEffect(() => {
    fetchPendientes();
  }, [filtro]);

  const traerMensaje = async () => {
    try {
      const r = await api.get('/mensajes-validacion/obtener-masivo');
      if (r.data.success && r.data.data) setMensajeValidacion(r.data.data.mensaje_whatsapp);
    } catch (e) {}
  };

  const fetchResumen = async () => {
    try {
      const r = await api.get('/validacion/resumen');
      setResumen(r.data || { pendientes: 0, confirmados: 0, descartados: 0, duplicados: 0 });
    } catch (e) {}
  };

  const fetchPendientes = async () => {
    try {
      setLoading(true);
      const r = await api.get('/validacion/pendientes?estatus=' + filtro);
      setPersonas(r.data.personas || []);
    } catch (e) {} finally { setLoading(false); }
  };

  const fetchSecciones = async () => {
    try {
      const r = await api.get('/estructuras/catalogos');
      setSecciones(r.data.secciones || []);
    } catch (e) {}
  };

  const fetchEstructurasYLideres = async () => {
    try {
      const [estRes, lidRes] = await Promise.all([api.get('/estructuras'), api.get('/auth/usuarios')]);
      setEstructuras(estRes.data.estructuras || []);
      setLideres((lidRes.data.usuarios || []).filter(u => ['coordinador_general','coordinador_territorial','coordinador_sectorial','seccional','operador','promotor'].includes(u.rol_nombre||u.rol||'')));
    } catch (e) {}
  };

  const handleSubirExcel = async () => {
    if (!archivoExcel) { setMensaje({ tipo: 'error', texto: 'Selecciona un archivo Excel.' }); return; }
    try {
      setSubiendo(true);
      const formData = new FormData();
      formData.append('archivo', archivoExcel);
      if (estructuraExcel) formData.append('estructura_id', estructuraExcel);
      if (liderExcel) formData.append('lider_inmediato_id', liderExcel);
      const r = await api.post('/validacion/subir', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMensaje({ tipo: 'exito', texto: `${r.data.total_subidos || 0} registros cargados.` });
      setArchivoExcel(null);
      fetchResumen();
      fetchPendientes();
    } catch (e) {
      setMensaje({ tipo: 'error', texto: e.response?.data?.error || 'Error al subir.' });
    } finally { setSubiendo(false); }
  };

  const confirmar = async (persona) => {
    try {
      await api.post('/validacion/confirmar/' + persona.id);
      setMensaje({ tipo: 'exito', texto: persona.nombre + ' ' + persona.apellido_paterno + ' confirmado.' });
      fetchResumen(); fetchPendientes();
      setTimeout(() => setMensaje(null), 3000);
    } catch (e) { setMensaje({ tipo: 'error', texto: e.response?.data?.error || 'Error.' }); }
  };

  const descartar = async (persona) => {
    if (!window.confirm('Descartar a ' + persona.nombre + ' ' + persona.apellido_paterno + '?')) return;
    try {
      await api.post('/validacion/descartar/' + persona.id);
      setMensaje({ tipo: 'exito', texto: persona.nombre + ' descartado.' });
      fetchResumen(); fetchPendientes();
      setTimeout(() => setMensaje(null), 3000);
    } catch (e) { setMensaje({ tipo: 'error', texto: 'Error al descartar.' }); }
  };

  const guardarEdicion = async (id) => {
    try {
      await api.put('/validacion/editar/' + id, formEditar);
      setMensaje({ tipo: 'exito', texto: 'Datos actualizados.' });
      setEditandoId(null); fetchPendientes();
      setTimeout(() => setMensaje(null), 3000);
    } catch (e) { setMensaje({ tipo: 'error', texto: e.response?.data?.error || 'Error al editar.' }); }
  };

  const marcarEnvio = async (persona, canal) => {
    try {
      await api.put('/validacion/marcar-envio/' + persona.id, { canal });
      setPersonas(prev => prev.map(p => p.id === persona.id ? { ...p, [canal + '_enviado']: true } : p));
    } catch (e) {}
  };

  const guardarMensaje = async () => {
    try {
      await api.put('/mensajes-validacion/actualizar-masivo', { mensaje_whatsapp: mensajeValidacion });
      setMensaje({ tipo: 'exito', texto: 'Mensaje guardado.' });
      setTimeout(() => setMensaje(null), 3000);
    } catch (e) {
      setMensaje({ tipo: 'error', texto: 'No se pudo guardar el mensaje.' });
      setTimeout(() => setMensaje(null), 3000);
    }
  };

  const abrirSMSNativo = async (p) => {
    const telOriginal = p.telefono;
    if (!telOriginal) {
      setMensaje({ tipo: 'error', texto: 'Este registro no tiene teléfono.' });
      return;
    }
    let tel = String(telOriginal).replace(/\D/g, '');
    if (tel.length === 10) tel = '52' + tel;
    let texto = mensajeValidacion || '';
    texto = texto.replace(/{NOMBRE}/gi, p.nombre || '');
    texto = texto.replace(/{APELLIDO_PATERNO}/gi, p.apellido_paterno || '');
    texto = texto.replace(/{APELLIDO_MATERNO}/gi, p.apellido_materno || '');
    const esIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const sep = esIOS ? '&' : '?';
    window.location.href = `sms:+${tel}${sep}body=${encodeURIComponent(texto)}`;
    marcarEnvio(p, 'sms');
  };

  const abrirWhatsAppNativo = async (p) => {
    const telOriginal = p.telefono;
    if (!telOriginal) {
      setMensaje({ tipo: 'error', texto: 'Este registro no tiene teléfono.' });
      return;
    }
    let tel = String(telOriginal).replace(/\D/g, '');
    if (tel.length === 10) tel = '52' + tel;
    let texto = mensajeValidacion || '';
    texto = texto.replace(/{NOMBRE}/gi, p.nombre || '');
    texto = texto.replace(/{APELLIDO_PATERNO}/gi, p.apellido_paterno || '');
    texto = texto.replace(/{APELLIDO_MATERNO}/gi, p.apellido_materno || '');
    window.open(`https://wa.me/${tel}?text=${encodeURIComponent(texto)}`, '_blank');
    marcarEnvio(p, 'whatsapp');
  };

  const handleEnviarSMSMasivo = async () => {
    const destinatarios = listaAMostrar.filter(p => p.telefono);
    if (destinatarios.length === 0) return setMensaje({ tipo: 'error', texto: 'No hay personas con teléfono en esta lista.' });
    const minutos = Math.ceil((destinatarios.length * 15) / 60);
    if (!window.confirm(`¿Enviar SMS a las ${destinatarios.length} personas de esta lista?\\n\\nTomará aprox ${minutos} min.`)) return;

    try {
      setEnviandoSMS(true);
      const datosEnvio = destinatarios.map(p => ({ telefono: p.telefono, nombre: p.nombre, persona_id: p.id, id_captura: p.id }));
      const textoBase = mensajeValidacion;
      const urlBot = "https://t.me/TuBotDeDominium"; 

      await api.post('/sms/enviar-masivo', { destinatarios: datosEnvio, textoBase, urlBot });
      setMensaje({ tipo: 'exito', texto: `✅ Envío iniciado para ${destinatarios.length} personas.` });
      setTimeout(() => { fetchResumen(); fetchPendientes(); }, 3000);
    } catch (e) {
      setMensaje({ tipo: 'error', texto: e.response?.data?.error || 'Error al iniciar envío.' });
    } finally { setEnviandoSMS(false); }
  };

  const personasFiltradas = personas.filter(p => {
    const texto = (p.nombre + ' ' + p.apellido_paterno + ' ' + (p.apellido_materno||'') + ' ' + (p.telefono||'')).toLowerCase();
    return texto.includes(busqueda.toLowerCase());
  });

  const sinContactar  = personasFiltradas.filter(p => !p.whatsapp_enviado && !p.sms_enviado);
  const contactados   = personasFiltradas.filter(p => p.whatsapp_enviado || p.sms_enviado);
  const listaAMostrar = filtro === 'pendiente'
    ? (subTabPendientes === 'sin_contactar' ? sinContactar : contactados)
    : personasFiltradas;

  return (
    <Layout>
      <div className="p-4 md:p-6 max-w-6xl mx-auto w-full">

        {/* ── Header ── */}
        <div className="mb-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-ink-900 flex items-center gap-2">
              <ClipboardList className="text-guinda-700" size={28} />
              Validación Masiva
            </h2>
            <p className="text-ink-500 text-sm mt-1">Revisa y confirma los registros del Excel del cliente</p>
          </div>
          <button onClick={() => { fetchResumen(); fetchPendientes(); }}
            className="bg-white hover:bg-ink-50 p-2.5 rounded-xl border border-ink-200 transition shadow-soft">
            <RefreshCw size={16} className={`text-ink-600 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* ── Mensaje global ── */}
        {mensaje && (
          <div className={`mb-4 p-4 rounded-xl text-sm font-medium border flex items-center justify-between ${
            mensaje.tipo === 'exito'
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}>
            <span>{mensaje.texto}</span>
            <button onClick={() => setMensaje(null)} className="hover:opacity-70">
              <X size={16} />
            </button>
          </div>
        )}

        {/* ── KPIs ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Pendientes',  valor: resumen.pendientes,  color: 'border-amber-500',  textColor: 'text-amber-600'  },
            { label: 'Confirmados', valor: resumen.confirmados, color: 'border-verde-600',  textColor: 'text-verde-700'  },
            { label: 'Descartados', valor: resumen.descartados, color: 'border-red-500',    textColor: 'text-red-600'    },
            { label: 'Duplicados',  valor: resumen.duplicados,  color: 'border-orange-400', textColor: 'text-orange-600' },
          ].map((c) => (
            <div key={c.label} className={`bg-white rounded-xl md:rounded-2xl shadow-soft border border-ink-100 p-3 text-center border-l-4 ${c.color}`}>
              <p className={`text-2xl font-bold ${c.textColor}`}>{c.valor || 0}</p>
              <p className="text-xs text-ink-500 mt-1">{c.label}</p>
            </div>
          ))}
        </div>

        {/* ── Panel Cargar Excel ── */}
        <div className="bg-white rounded-2xl shadow-soft border border-ink-100 p-4 md:p-5 mb-4">
          <p className="text-sm font-semibold text-ink-900 mb-3 flex items-center gap-2">
            📥 Cargar Excel del cliente
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
            <select value={estructuraExcel} onChange={e => setEstructuraExcel(e.target.value)}
              className="px-3 py-2.5 border border-ink-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-guinda-700/20 focus:border-guinda-700">
              <option value="">Estructura (opcional)</option>
              {estructuras.map(e => <option key={e.id} value={e.id}>{e.nombre_estructura}</option>)}
            </select>
            <select value={perfilLiderExcel}
              onChange={e => { setPerfilLiderExcel(e.target.value); setLiderExcel(''); }}
              className="px-3 py-2.5 border border-ink-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-guinda-700/20 focus:border-guinda-700">
              <option value="">Perfil del líder (opcional)</option>
              {PERFILES_LIDER.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <select value={liderExcel} onChange={e => setLiderExcel(e.target.value)}
              disabled={!perfilLiderExcel}
              className="px-3 py-2.5 border border-ink-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-guinda-700/20 focus:border-guinda-700 disabled:bg-ink-50 disabled:text-ink-400">
              <option value="">{perfilLiderExcel ? 'Selecciona el nombre' : 'Elige un perfil primero'}</option>
              {lideres.filter(l => (l.rol_nombre||l.rol||'') === perfilLiderExcel)
                .map(l => <option key={l.id} value={l.id}>{l.nombre} {l.apellido_paterno}</option>)}
            </select>
            <div className="flex gap-2">
              <label className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 border-2 border-dashed border-ink-300 rounded-xl cursor-pointer hover:border-guinda-700 hover:bg-guinda-700/5 text-sm text-ink-600 transition min-w-0">
                <Upload size={14} className="shrink-0" />
                <span className="truncate">{archivoExcel ? archivoExcel.name : 'Seleccionar .xlsx'}</span>
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={e => setArchivoExcel(e.target.files[0])} />
              </label>
              <button onClick={handleSubirExcel} disabled={subiendo || !archivoExcel}
                className="px-4 py-2 bg-guinda-700 hover:bg-guinda-800 text-white rounded-xl text-sm font-semibold disabled:opacity-50 transition shadow-soft">
                {subiendo ? '...' : 'Subir'}
              </button>
            </div>
          </div>
          <a href="/api/validacion/plantilla" download
            className="inline-flex items-center gap-2 px-3 py-2 border border-ink-200 rounded-xl text-sm text-guinda-700 hover:bg-guinda-700/5 font-medium transition">
            📥 Descargar plantilla
          </a>
        </div>

        {/* ── Panel Mensaje ── */}
        <div className="bg-guinda-700/5 border border-guinda-700/20 rounded-2xl p-4 md:p-5 mb-4">
          <p className="text-sm font-semibold text-ink-900 mb-2 flex items-center gap-2">
            📝 Mensaje de Validación (WhatsApp y SMS)
          </p>
          <textarea value={mensajeValidacion} onChange={e => setMensajeValidacion(e.target.value)}
            rows={4}
            className="w-full px-3 py-2.5 border border-ink-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-guinda-700/20 focus:border-guinda-700 resize-none"
            placeholder="Escribe el mensaje..." />
          <div className="flex items-center justify-between flex-wrap gap-2 mt-3">
            <p className="text-xs text-ink-500">
              💡 Usa <code className="px-1 py-0.5 bg-white rounded text-guinda-700 font-mono">{'{NOMBRE}'}</code>,{' '}
              <code className="px-1 py-0.5 bg-white rounded text-guinda-700 font-mono">{'{APELLIDO_PATERNO}'}</code> o{' '}
              <code className="px-1 py-0.5 bg-white rounded text-guinda-700 font-mono">{'{APELLIDO_MATERNO}'}</code>
            </p>
            <div className="flex gap-2">
              <button onClick={guardarMensaje}
                className="flex items-center gap-2 px-4 py-2 bg-guinda-700 hover:bg-guinda-800 text-white rounded-xl text-sm font-semibold transition shadow-soft">
                <Save size={14} /> Guardar Mensaje
              </button>
              <button onClick={handleEnviarSMSMasivo} disabled={enviandoSMS}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold transition shadow-soft disabled:opacity-50">
                <Send size={14} /> {enviandoSMS ? 'Iniciando...' : 'Enviar SMS Masivo'}
              </button>
            </div>
          </div>
        </div>

        {/* ── Filtros + Búsqueda ── */}
        <div className="flex flex-wrap gap-2 mb-4 items-center">
          <div className="flex gap-1 border border-ink-200 rounded-xl p-1 bg-white">
            {[
              { key: 'pendiente',  label: 'Pendientes',  dot: 'bg-amber-500' },
              { key: 'confirmado', label: 'Confirmados', dot: 'bg-verde-600' },
              { key: 'descartado', label: 'Descartados', dot: 'bg-red-500' },
            ].map(f => (
              <button key={f.key} onClick={() => setFiltro(f.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                  filtro === f.key
                    ? 'bg-guinda-700 text-white shadow-soft'
                    : 'text-ink-600 hover:bg-ink-50'
                }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${filtro === f.key ? 'bg-white' : f.dot}`}></span>
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex-1 relative min-w-48">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
            <input value={busqueda} onChange={e => setBusqueda(e.target.value)}
              placeholder="Buscar por nombre, teléfono o sección..."
              className="w-full pl-9 pr-3 py-2.5 border border-ink-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-guinda-700/20 focus:border-guinda-700" />
          </div>
        </div>

        <p className="text-sm text-ink-500 mb-3">
          <span className="font-semibold text-ink-900">{listaAMostrar.length}</span> registro(s)
        </p>

        {/* ── Sub-tabs (solo en pendientes) ── */}
        {filtro === 'pendiente' && !loading && personasFiltradas.length > 0 && (
          <div className="flex gap-2 mb-4 border-b border-ink-100 overflow-x-auto">
            <button onClick={() => setSubTabPendientes('sin_contactar')}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition whitespace-nowrap ${
                subTabPendientes === 'sin_contactar'
                  ? 'border-amber-500 text-amber-700'
                  : 'border-transparent text-ink-500 hover:text-ink-700'
              }`}>
              ⏳ Sin contactar ({sinContactar.length})
            </button>
            <button onClick={() => setSubTabPendientes('contactados')}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition whitespace-nowrap ${
                subTabPendientes === 'contactados'
                  ? 'border-guinda-700 text-guinda-700'
                  : 'border-transparent text-ink-500 hover:text-ink-700'
              }`}>
              📨 Ya contactados ({contactados.length})
            </button>
          </div>
        )}

        {/* ── Listado ── */}
        {loading ? (
          <div className="bg-white rounded-2xl shadow-soft border border-ink-100 p-12 text-center text-ink-400 text-sm">
            Cargando registros...
          </div>
        ) : listaAMostrar.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-soft border border-ink-100 p-12 text-center">
            <Users size={48} className="mx-auto mb-3 text-ink-200" />
            <p className="text-ink-500 font-medium">
              {filtro === 'pendiente' && subTabPendientes === 'sin_contactar'
                ? 'No hay nadie sin contactar 🎉'
                : filtro === 'pendiente' && subTabPendientes === 'contactados'
                ? 'Todavía no le has mandado mensaje a nadie'
                : 'No hay registros'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {listaAMostrar.map((p) => (
              <div key={p.id} className={`rounded-2xl shadow-soft border p-4 transition ${
                filtro === 'pendiente' && subTabPendientes === 'contactados'
                  ? 'bg-guinda-700/5 border-guinda-700/20'
                  : 'bg-white border-ink-100'
              }`}>
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <p className="font-semibold text-ink-900">
                        {p.apellido_paterno} {p.apellido_materno || ''} {p.nombre}
                      </p>
                      {p.duplicado_detectado && (
                        <span className="flex items-center gap-1 bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide">
                          <AlertTriangle size={10} /> Duplicado
                        </span>
                      )}
                      {filtro === 'pendiente' && subTabPendientes === 'contactados' ? (
                        <span className="px-2 py-0.5 bg-rose-100 text-rose-700 border border-rose-200 rounded-full text-[10px] font-bold uppercase tracking-wide">
                          📨 Esperando respuesta
                        </span>
                      ) : (
                        <span className={`px-2 py-0.5 border rounded-full text-[10px] font-bold uppercase tracking-wide ${
                          ESTATUS_BADGE[p.estatus] || 'bg-ink-100 text-ink-600 border-ink-200'
                        }`}>
                          {p.estatus}
                        </span>
                      )}
                    </div>

                    {p.duplicado_info && (
                      <p className="text-xs text-amber-700 mb-1 font-medium">⚠️ {p.duplicado_info}</p>
                    )}

                    {editandoId === p.id ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                        {[
                          { name: 'nombre',          label: 'Nombre' },
                          { name: 'apellido_paterno', label: 'Ap. Paterno' },
                          { name: 'apellido_materno', label: 'Ap. Materno' },
                          { name: 'telefono',        label: 'Teléfono' },
                          { name: 'clave_elector',   label: 'Clave Elector' },
                        ].map(campo => (
                          <div key={campo.name}>
                            <label className="block text-[10px] uppercase tracking-wide text-ink-400 font-semibold mb-1">{campo.label}</label>
                            <input value={formEditar[campo.name] || ''} onChange={e => setFormEditar(prev => ({ ...prev, [campo.name]: e.target.value }))}
                              className="w-full px-2.5 py-1.5 border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-guinda-700/20 focus:border-guinda-700" />
                          </div>
                        ))}
                        <div className="sm:col-span-2">
                          <label className="block text-[10px] uppercase tracking-wide text-ink-400 font-semibold mb-1">Sección</label>
                          <select value={formEditar.seccion_id || ''} onChange={e => setFormEditar(prev => ({ ...prev, seccion_id: e.target.value }))}
                            className="w-full px-2.5 py-1.5 border border-ink-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-guinda-700/20 focus:border-guinda-700">
                            <option value="">Seleccionar...</option>
                            {secciones.map(s => <option key={s.id} value={s.id}>{s.numero_seccion} - {s.municipio}</option>)}
                          </select>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-ink-500 mt-1">
                        {p.telefono && <span>📞 {p.telefono}</span>}
                        {p.seccion_numero && <span>🗺 Sección {p.seccion_numero}</span>}
                        {p.localidad && <span>📍 {p.localidad}</span>}
                        {p.nivel && <span>📋 {p.nivel}</span>}
                      </div>
                    )}
                  </div>

                  {/* Botones de acción */}
                  <div className="flex flex-wrap gap-2 items-center">
                    {filtro === 'pendiente' && (
                      <>
                        {p.telefono && (
                          <>
                            <button onClick={() => abrirWhatsAppNativo(p)}
                              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-white transition shadow-soft"
                              style={{ background: p.whatsapp_enviado ? '#128C7E' : '#25D366' }}>
                              {p.whatsapp_enviado ? '✅' : '📱'} WhatsApp
                            </button>
                            <button onClick={() => abrirSMSNativo(p)}
                              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-white transition shadow-soft"
                              style={{ background: p.sms_enviado ? '#1a4a8a' : '#2563a8' }}>
                              {p.sms_enviado ? '✅' : '💬'} SMS
                            </button>
                          </>
                        )}
                        {editandoId === p.id ? (
                          <>
                            <button onClick={() => guardarEdicion(p.id)}
                              className="flex items-center gap-1.5 px-3 py-2 bg-verde-600 hover:bg-verde-700 text-white rounded-xl text-xs font-semibold transition shadow-soft">
                              <Save size={14} /> Guardar
                            </button>
                            <button onClick={() => setEditandoId(null)}
                              className="px-3 py-2 border border-ink-200 rounded-xl text-xs text-ink-600 hover:bg-ink-50 font-semibold transition">
                              Cancelar
                            </button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => confirmar(p)}
                              className="flex items-center gap-1.5 px-3 py-2 bg-verde-600 hover:bg-verde-700 text-white rounded-xl text-xs font-semibold transition shadow-soft">
                              <CheckCircle size={14} /> Confirmar
                            </button>
                            <button onClick={() => { setEditandoId(p.id); setFormEditar({ nombre: p.nombre, apellido_paterno: p.apellido_paterno, apellido_materno: p.apellido_materno, telefono: p.telefono, clave_elector: p.clave_elector, seccion_id: p.seccion_id }); }}
                              className="flex items-center gap-1.5 px-3 py-2 bg-guinda-700 hover:bg-guinda-800 text-white rounded-xl text-xs font-semibold transition shadow-soft">
                              <Edit size={14} /> Editar
                            </button>
                            <button onClick={() => descartar(p)}
                              className="flex items-center gap-1.5 px-3 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-xl text-xs font-semibold transition">
                              <XCircle size={14} /> Rechazar
                            </button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {p.respuesta_recibida && (
                  <div className="mt-3 px-3 py-2 bg-white border border-guinda-700/20 rounded-xl text-xs">
                    <span className="font-semibold text-guinda-700">Respondió:</span>{' '}
                    <span className="text-ink-700">{p.respuesta_recibida}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <p className="text-center text-ink-400 text-xs mt-8">
          DOMINIUM © 2026 — Zacatlán, Puebla | GARCOR Design
        </p>
      </div>
    </Layout>
  );
}
